import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyDocument, validateReferenceFormat, formatReference } from '../services/verificationService';

const DocumentVerification = () => {
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(true);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [autoVerifying, setAutoVerifying] = useState(false);

  // Gestion automatique via paramètre URL
  useEffect(() => {
    const refParam = searchParams.get('ref');
    
    if (refParam) {
      // Référence trouvée dans l'URL - vérification automatique
      const formattedRef = formatReference(refParam);
      const validation = validateReferenceFormat(formattedRef);
      
      if (validation.isValid) {
        setReference(formattedRef);
        setAutoVerifying(true);
        setShowModal(false);
        performVerification(formattedRef);
      } else {
        // Référence invalide - afficher le modal avec l'erreur
        setReference(formattedRef);
        setValidationError(validation.message);
        setShowModal(true);
      }
    } else {
      // Pas de paramètre - afficher le modal de saisie
      setShowModal(true);
    }
  }, [searchParams]);

  const handleReferenceChange = (e) => {
    const value = e.target.value;
    const formatted = formatReference(value);
    setReference(formatted);
    
    // Validation en temps réel
    if (formatted) {
      const validation = validateReferenceFormat(formatted);
      setValidationError(validation.isValid ? '' : validation.message);
    } else {
      setValidationError('');
    }
  };

  // Fonction de vérification réutilisable
  const performVerification = async (refToVerify) => {
    setLoading(true);
    setError('');
    setValidationError('');

    try {
      const result = await verifyDocument(refToVerify);
      
      if (result.success) {
        setVerificationResult(result.data);
        setShowModal(false);
      } else {
        setError(result.error);
        // En cas d'erreur avec verification automatique, afficher le modal
        if (autoVerifying) {
          setShowModal(true);
        }
      }
    } catch (err) {
      setError('Erreur inattendue lors de la vérification');
      console.error('Erreur vérification:', err);
      // En cas d'erreur avec verification automatique, afficher le modal
      if (autoVerifying) {
        setShowModal(true);
      }
    } finally {
      setLoading(false);
      setAutoVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation finale
    const validation = validateReferenceFormat(reference);
    if (!validation.isValid) {
      setValidationError(validation.message);
      return;
    }

    await performVerification(reference);
  };

  const handleReset = () => {
    setReference('');
    setError('');
    setValidationError('');
    setVerificationResult(null);
    setAutoVerifying(false);
    setShowModal(true);
    
    // Nettoyer l'URL si elle contient des paramètres
    if (searchParams.get('ref')) {
      window.history.pushState({}, '', window.location.pathname);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'VALID': {
        style: 'bg-green-100 text-green-800 border-green-300',
        icon: '✅',
        label: 'Valide et Authentique'
      },
      'EXPIRED': {
        style: 'bg-red-100 text-red-800 border-red-300',
        icon: '⚠️',
        label: 'Document Expiré'
      },
      'NOT_FOUND': {
        style: 'bg-red-100 text-red-800 border-red-300',
        icon: '❌',
        label: 'Document Non Authentique'
      },
      'NOT_DELIVERED': {
        style: 'bg-orange-100 text-orange-800 border-orange-300',
        icon: '⏳',
        label: 'Document Non Livré'
      },
      'SUPPRIMEE': {
        style: 'bg-red-100 text-red-800 border-red-300',
        icon: '❌',
        label: 'Document Non Valide'
      }
    };

    const config = configs[status] || {
      style: 'bg-gray-100 text-gray-800 border-gray-300',
      icon: '❓',
      label: 'Statut Inconnu'
    };

    return (
      <div className={`inline-flex items-center px-6 py-3 rounded-lg border-2 font-semibold text-lg ${config.style} shadow-md`}>
        <span className="mr-3 text-2xl">{config.icon}</span>
        <span>{config.label}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-orange-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center space-x-4">
              {/* Logo Boaz-Housing */}
              <div className="flex-shrink-0">
                <img 
                  src="/logo-boaz-housing.png" 
                  alt="Boaz-Housing Logo" 
                  className="w-32 h-32 object-contain hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    // Fallback vers l'icône si le logo n'est pas trouvé
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-32 h-32 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg flex items-center justify-center shadow-lg" style={{display: 'none'}}>
                  <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Vérification de Document</h1>
                <p className="text-sm text-gray-600">
                  Boaz-Housing - Attestation d'Hébergement
                  {searchParams.get('ref') && (
                    <span className="ml-2 text-blue-600 font-medium">(· Réf: {searchParams.get('ref')})</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        
        {/* Écran de chargement pour vérification automatique */}
        {autoVerifying && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-orange-500 px-6 py-8 text-white text-center">
              {/* Logo en en-tête de l'écran de chargement */}
              <div className="mb-4">
                <img 
                  src="/logo-boaz-housing.png" 
                  alt="Boaz-Housing Logo" 
                  className="w-20 h-20 object-contain mx-auto mb-4 opacity-90 drop-shadow-lg"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div className="animate-spin rounded-full h-14 w-14 border-b-3 border-white mx-auto shadow-lg"></div>
              </div>
              <h2 className="text-2xl font-bold mb-2">Vérification en cours...</h2>
              <p className="text-blue-100">Référence: <span className="font-mono font-bold text-white">{reference}</span></p>
            </div>
          </div>
        )}

        {/* Modal de Saisie */}
        {showModal && !autoVerifying && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform animate-pulse-fade">
              <div className="px-6 py-6 border-b border-gray-200 text-center">
                {/* Logo en haut et centré */}
                <div className="mb-4">
                  <img 
                    src="/logo-boaz-housing.png" 
                    alt="Boaz-Housing Logo" 
                    className="w-40 h-40 object-contain mx-auto hover:scale-105 transition-transform duration-200 drop-shadow-lg"
                    onError={(e) => {
                      // Fallback vers l'icône si le logo n'est pas trouvé
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-40 h-40 bg-gradient-to-r from-blue-600 to-orange-500 rounded-xl flex items-center justify-center shadow-lg mx-auto" style={{display: 'none'}}>
                    <svg className="w-20 h-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                </div>
                
                {/* Titre et sous-titre centrés */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Vérifier un Document</h2>
                  <p className="text-sm text-gray-600">Boaz-Housing - Attestation d'Hébergement</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="mb-6">
                  <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-2">
                    Référence du Document
                  </label>
                  <input
                    type="text"
                    id="reference"
                    value={reference}
                    onChange={handleReferenceChange}
                    placeholder="Ex: ATT-D28B8C5877C1CA25"
                    className={`w-full px-4 py-3 border-2 rounded-lg font-mono text-center text-lg tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationError 
                        ? 'border-red-300 bg-red-50 focus:border-red-500' 
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    disabled={loading}
                    autoFocus
                  />
                  {validationError && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                      </svg>
                      {validationError}
                    </p>
                  )}
                  {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                      </svg>
                      {error}
                    </p>
                  )}
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={loading || !reference.trim() || validationError}
                    className="w-full bg-gradient-to-r from-blue-600 to-orange-500 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-orange-600 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Vérification...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Vérifier le Document
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Résultats de Vérification */}
        {verificationResult && !showModal && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header du Résultat */}
            <div className="bg-gradient-to-r from-blue-600 to-orange-500 px-6 py-8 text-white">
              <div className="text-center">
                <div className="mb-4">
                  {getStatusBadge(verificationResult.status)}
                </div>
                <h2 className="text-2xl font-bold mb-2">Résultat de Vérification</h2>
                <p className="text-blue-100">Référence: <span className="font-mono font-bold text-white">{reference}</span></p>
                <p className="text-blue-100 text-sm mt-1">Vérification effectuée le {verificationResult.verification_date}</p>
              </div>
            </div>

            {/* Message Principal */}
            <div className="px-6 py-6 border-b border-gray-200">
              <div className={`p-4 rounded-lg ${
                verificationResult.valid 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-lg font-medium ${
                  verificationResult.valid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {verificationResult.message}
                </p>
              </div>
            </div>

            {/* Détails de la Souscription */}
            {verificationResult.souscription_data && (
              <div className="px-6 py-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Informations du Document
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-600 mb-1">Bénéficiaire</dt>
                    <dd className="text-lg font-semibold text-gray-900">{verificationResult.souscription_data.client_name}</dd>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <dt className="text-sm font-medium text-gray-600 mb-1">Statut</dt>
                    <dd className="text-lg font-semibold text-gray-900 capitalize">
                      {verificationResult.souscription_data.status === 'LIVRE' ? 'Livré' : 
                       verificationResult.souscription_data.status === 'CLOTURE' ? 'Clôturé' :
                       verificationResult.souscription_data.status === 'SUPPRIMEE' ? 'Supprimée' :
                       verificationResult.souscription_data.status}
                    </dd>
                  </div>

                  {verificationResult.souscription_data.delivery_date && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-600 mb-1">Date de Livraison</dt>
                      <dd className="text-lg font-semibold text-gray-900">{verificationResult.souscription_data.delivery_date}</dd>
                    </div>
                  )}

                  {verificationResult.expiration_date && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-600 mb-1">Date d'Expiration</dt>
                      <dd className={`text-lg font-semibold ${
                        verificationResult.valid ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {verificationResult.expiration_date}
                      </dd>
                    </div>
                  )}

                  {verificationResult.souscription_data.logement_address && (
                    <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                      <dt className="text-sm font-medium text-gray-600 mb-1">Adresse du Logement</dt>
                      <dd className="text-lg font-semibold text-gray-900">{verificationResult.souscription_data.logement_address}</dd>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center">
              <button
                onClick={handleReset}
                className="bg-gradient-to-r from-blue-600 to-orange-500 text-white py-2 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-orange-600 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all duration-200"
              >
                Vérifier un Autre Document
              </button>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              À propos de la Vérification
              <span className="ml-2 text-sm font-normal text-blue-600">Boaz-Housing</span>
            </h3>
            <p className="text-blue-800 leading-relaxed mb-3">
              Ce service permet de vérifier l'authenticité et la validité des attestations d'hébergement et de prise en charge émises par Boaz-Housing.
            </p>
            <div className="text-blue-800 text-sm">
              <p className="mb-1">• <strong>Saisie manuelle :</strong> Entrez la référence (format ATT-XXXXXX)</p>
              <p className="mb-1">• <strong>Lien direct :</strong> Utilisez l'URL avec paramètre ?ref=ATT-XXXXXX</p>
              <p>• <strong>QR Code :</strong> Scannez le QR code sur le document pour vérification automatique</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentVerification;